import { useQuery } from "@tanstack/react-query";
import { fetchWithAuth } from "../lib/api";

export interface ClickData {
  link_id: number;
  link_label?: string;
  clicked_at?: string;
  suspected_bot: boolean;
}

export interface TrackingData {
  tracking_id: string;
  source_type: string;
  send_status: string;
  sent_at?: string;
  first_opened_at?: string;
  last_opened_at?: string;
  open_count: number;
  suspected_proxy: boolean;
  clicks: ClickData[];
}

async function fetchTrackingBatch(
  trackingIds: string[]
): Promise<TrackingData[]> {
  return fetchWithAuth<TrackingData[]>("/email-tracking/batch", {
    method: "POST",
    body: JSON.stringify({ tracking_ids: trackingIds }),
  });
}

/**
 * Batch fetch email tracking data for multiple tracking IDs.
 * Returns a map of tracking_id -> TrackingData for easy lookup.
 */
export function useEmailTracking(trackingIds: string[]) {
  const filtered = trackingIds.filter(Boolean);

  const query = useQuery({
    queryKey: ["email-tracking", ...filtered.sort()],
    queryFn: () => fetchTrackingBatch(filtered),
    staleTime: 30_000,
    enabled: filtered.length > 0,
  });

  // Build a lookup map for convenience
  const trackingMap = new Map<string, TrackingData>();
  if (query.data) {
    for (const item of query.data) {
      trackingMap.set(item.tracking_id, item);
    }
  }

  return {
    ...query,
    trackingMap,
  };
}
